/**
 * Validation Utility
 * Comprehensive validation rules for Capital Management System
 */

import { parse, isAfter, isBefore, addMonths, addYears, differenceInDays } from 'date-fns';

/**
 * Number formatting utilities
 * Standard format: space for thousands separator, period for decimal.
 *
 * DUPLICATION NOTE: `./formatters.js` also exports `formatNumber` and `formatCurrency`
 * with different output (Intl.NumberFormat with en-ZA locale: "1,000.50" and "R 1,000.50").
 * These two implementations are NOT interchangeable — they produce visibly different
 * strings. Callers must pick one deliberately.
 * Prefer importing from `./formatters` unless you specifically need the space-thousands
 * "ZAR 1 000.50" output (e.g., CSV export, reports with fixed-width layouts).
 */
export const formatNumber = (value, decimals = 2) => {
  if (value === null || value === undefined || value === '') return '-';
  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(num)) return '-';

  // Format with period as decimal separator
  const formatted = num.toFixed(decimals);

  // Split into integer and decimal parts
  const [intPart, decPart] = formatted.split('.');

  // Add space as thousand separator to integer part
  const withThousands = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');

  return decPart ? `${withThousands}.${decPart}` : withThousands;
};

/**
 * Format currency with code
 */
export const formatCurrency = (value, currencyCode = 'ZAR', decimals = 2) => {
  if (value === null || value === undefined || value === '') return '-';
  return `${currencyCode} ${formatNumber(value, decimals)}`;
};

/**
 * Contract validation rules
 */
export const contractValidation = {
  /**
   * Validate deposit date
   * Note: Future deposit dates ARE allowed for pipeline/planning contracts.
   * Interest accrual is based on funds_received_date, not deposit_date.
   */
  validateDepositDate: (depositDate) => {
    const deposit = new Date(depositDate);

    // Check for valid date
    if (isNaN(deposit.getTime())) {
      return { valid: false, message: 'Invalid deposit date' };
    }

    // Allow future dates - contracts can be set up in advance
    return { valid: true };
  },

  /**
   * Validate maturity date is after deposit date
   */
  validateMaturityDate: (depositDate, maturityDate) => {
    const deposit = new Date(depositDate);
    const maturity = new Date(maturityDate);

    if (!isAfter(maturity, deposit)) {
      return { valid: false, message: 'Maturity date must be after deposit date' };
    }

    // Check minimum term (at least 1 day)
    const daysDiff = differenceInDays(maturity, deposit);
    if (daysDiff < 1) {
      return { valid: false, message: 'Investment term must be at least 1 day' };
    }

    return { valid: true };
  },

  /**
   * Validate investment term matches dates
   */
  validateInvestmentTerm: (depositDate, maturityDate, termValue, termUnit) => {
    const deposit = new Date(depositDate);
    let expectedMaturity;

    switch (termUnit) {
      case 'Days':
        expectedMaturity = new Date(deposit);
        expectedMaturity.setDate(deposit.getDate() + parseInt(termValue));
        break;
      case 'Months':
        expectedMaturity = addMonths(deposit, parseInt(termValue));
        break;
      case 'Years':
        expectedMaturity = addYears(deposit, parseInt(termValue));
        break;
      default:
        return { valid: false, message: 'Invalid investment term unit' };
    }

    const actualMaturity = new Date(maturityDate);
    const daysDiff = Math.abs(differenceInDays(expectedMaturity, actualMaturity));

    // Allow up to 5 days difference for business day adjustments
    if (daysDiff <= 5) {
      return { valid: true };
    }

    // Accept maturity intentionally rolled to month-end of the expected month
    const sameYear = expectedMaturity.getFullYear() === actualMaturity.getFullYear();
    const sameMonth = expectedMaturity.getMonth() === actualMaturity.getMonth();
    const lastDayOfActualMonth = new Date(
      actualMaturity.getFullYear(),
      actualMaturity.getMonth() + 1,
      0
    ).getDate();
    const isMonthEnd = actualMaturity.getDate() === lastDayOfActualMonth;
    if (sameYear && sameMonth && isMonthEnd) {
      return { valid: true };
    }

    return {
      valid: false,
      message: `Maturity date does not match investment term. Expected around ${expectedMaturity.toLocaleDateString()}`,
    };
  },

  /**
   * Validate capital amount
   */
  validateCapitalAmount: (amount, currencyCode) => {
    const numAmount = parseFloat(amount);

    if (isNaN(numAmount) || numAmount <= 0) {
      return { valid: false, message: 'Capital amount must be greater than zero' };
    }

    return { valid: true };
  },

  /**
   * Validate interest rate
   */
  validateInterestRate: (rate, rateType) => {
    const numRate = parseFloat(rate);

    if (isNaN(numRate)) {
      return { valid: false, message: 'Interest rate must be a valid number' };
    }

    if (numRate <= 0) {
      return { valid: false, message: 'Interest rate must be greater than 0' };
    }

    return { valid: true };
  },

  /**
   * Validate broker fees
   */
  validateBrokerFees: (fees) => {
    const numFees = parseFloat(fees);

    if (isNaN(numFees)) {
      return { valid: false, message: 'Broker fees must be a valid number' };
    }

    if (numFees < 0) {
      return { valid: false, message: 'Broker fees cannot be negative' };
    }

    return { valid: true };
  },

  /**
   * Validate stamp duty fees
   */
  validateStampDutyFees: (fees) => {
    const numFees = parseFloat(fees);

    if (isNaN(numFees)) {
      return { valid: false, message: 'Stamp duty fees must be a valid number' };
    }

    if (numFees < 0) {
      return { valid: false, message: 'Stamp duty fees cannot be negative' };
    }

    return { valid: true };
  },

  /**
   * Validate collateral amount
   */
  validateCollateralAmount: (collateralAmount, capitalAmount) => {
    const numCollateral = parseFloat(collateralAmount);
    const numCapital = parseFloat(capitalAmount);

    if (isNaN(numCollateral) || numCollateral <= 0) {
      return { valid: false, message: 'Collateral amount must be greater than zero' };
    }

    // Collateral should typically be at least equal to capital
    if (numCollateral < numCapital) {
      return {
        valid: true,
        warning: 'Collateral amount is less than capital amount. Please verify this is correct.',
      };
    }

    return { valid: true };
  },

  /**
   * Validate payment frequency compatibility
   * Uses months_value from reference data when available (more reliable than name matching)
   */
  validatePaymentFrequency: (interestFreq, capitalFreq, interestMonths = null, capitalMonths = null) => {
    // Capital payment frequency must be same or less frequent than interest

    // If months values are provided from reference data, use them directly
    if (interestMonths !== null && interestMonths !== undefined &&
        capitalMonths !== null && capitalMonths !== undefined) {
      // Higher months_value = less frequent (e.g., 12 = yearly, 1 = monthly)
      // Capital should be same or higher (less frequent) than interest
      if (capitalMonths < interestMonths) {
        return {
          valid: false,
          message: 'Capital payment frequency cannot be more frequent than interest payment frequency',
        };
      }
      return { valid: true };
    }

    // Fallback: Support multiple naming conventions from different data sources
    const frequencyOrder = {
      'Daily': 1,
      'Weekly': 2,
      'Bi-Weekly': 3,
      'Monthly': 4,
      'Bi-Monthly': 5,
      'Quarterly': 6,
      'Semi-Annually': 7,
      'Bi-Annual': 7,
      'Bi_Annualy': 7,
      'Bi-Annually': 7,
      'Annually': 8,
      'Annual': 8,
      'Yearly': 8,
      'At Maturity': 99,
    };

    const interestOrder = frequencyOrder[interestFreq] || 0;
    const capitalOrder = frequencyOrder[capitalFreq] || 0;

    // If frequency not recognized, skip validation
    if (interestOrder === 0 || capitalOrder === 0) {
      return { valid: true };
    }

    if (capitalOrder < interestOrder && capitalFreq !== 'At Maturity') {
      return {
        valid: false,
        message: 'Capital payment frequency cannot be more frequent than interest payment frequency',
      };
    }

    return { valid: true };
  },

  /**
   * Validate contract reference format
   */
  validateContractReference: (reference) => {
    if (!reference || reference.trim().length === 0) {
      return { valid: false, message: 'Contract reference is required' };
    }

    // Expected format: PRODUCT-YYYY-NNN (e.g., MTN-2024-001)
    const pattern = /^[A-Z]+-\d{4}-\d{3,}$/;
    if (!pattern.test(reference)) {
      return {
        valid: false,
        message: 'Contract reference must follow format: PRODUCT-YYYY-NNN (e.g., MTN-2024-001)',
      };
    }

    return { valid: true };
  },

  /**
   * Validate complete contract
   */
  validateContract: (contract) => {
    const errors = [];
    const warnings = [];

    // Required fields
    if (!contract.investor_id) {
      errors.push('Investor is required');
    }
    if (!contract.product_type) {
      errors.push('Product type is required');
    }
    if (!contract.currency_code) {
      errors.push('Currency is required');
    }
    if (!contract.deposit_date) {
      errors.push('Deposit date is required');
    }
    if (!contract.maturity_date) {
      errors.push('Maturity date is required');
    }

    // Deposit date validation
    if (contract.deposit_date) {
      const depositResult = contractValidation.validateDepositDate(contract.deposit_date);
      if (!depositResult.valid) {
        errors.push(depositResult.message);
      }
    }

    // Maturity date validation
    if (contract.deposit_date && contract.maturity_date) {
      const maturityResult = contractValidation.validateMaturityDate(
        contract.deposit_date,
        contract.maturity_date
      );
      if (!maturityResult.valid) {
        errors.push(maturityResult.message);
      }
    }

    // Investment term validation
    if (
      contract.deposit_date &&
      contract.maturity_date &&
      contract.investment_term_value &&
      contract.investment_term
    ) {
      const termResult = contractValidation.validateInvestmentTerm(
        contract.deposit_date,
        contract.maturity_date,
        contract.investment_term_value,
        contract.investment_term
      );
      if (!termResult.valid) {
        errors.push(termResult.message);
      }
    }

    // Capital amount validation
    if (contract.capital_amount && contract.currency_code) {
      const amountResult = contractValidation.validateCapitalAmount(
        contract.capital_amount,
        contract.currency_code
      );
      if (!amountResult.valid) {
        errors.push(amountResult.message);
      }
    }

    // Interest rate validation — must run even when rate is 0 or missing
    if (contract.rate_type) {
      const rateResult = contractValidation.validateInterestRate(
        contract.interest_rate,
        contract.rate_type
      );
      if (!rateResult.valid) {
        errors.push(rateResult.message);
      } else if (rateResult.warning) {
        warnings.push(rateResult.warning);
      }
    }

    // Broker fees validation
    if (contract.is_brokered && contract.broker_fees) {
      const feesResult = contractValidation.validateBrokerFees(contract.broker_fees);
      if (!feesResult.valid) {
        errors.push(feesResult.message);
      }
    }

    // Stamp duty validation
    if (contract.stamp_duty && contract.stamp_duty_fees) {
      const stampResult = contractValidation.validateStampDutyFees(contract.stamp_duty_fees);
      if (!stampResult.valid) {
        errors.push(stampResult.message);
      }
    }

    // Collateral validation
    if (contract.has_collateral && contract.collateral_amount && contract.capital_amount) {
      const collateralResult = contractValidation.validateCollateralAmount(
        contract.collateral_amount,
        contract.capital_amount
      );
      if (!collateralResult.valid) {
        errors.push(collateralResult.message);
      } else if (collateralResult.warning) {
        warnings.push(collateralResult.warning);
      }
    }

    // Payment frequency validation
    if (contract.interest_paid_frequency && contract.capital_paid_frequency) {
      const freqResult = contractValidation.validatePaymentFrequency(
        contract.interest_paid_frequency,
        contract.capital_paid_frequency,
        contract.interest_paid_frequency_months,
        contract.capital_paid_frequency_months
      );
      if (!freqResult.valid) {
        errors.push(freqResult.message);
      }
    }

    // Contract reference validation (for approved contracts)
    if (contract.reference && contract.status !== 'Draft') {
      const refResult = contractValidation.validateContractReference(contract.reference);
      if (!refResult.valid) {
        errors.push(refResult.message);
      }
    }

    return {
      valid: errors.length === 0,
      errors,
      warnings,
    };
  },
};

/**
 * Stakeholder validation rules
 */
export const stakeholderValidation = {
  /**
   * Validate email format
   */
  validateEmail: (email) => {
    if (!email) {
      return { valid: true }; // Email is optional
    }

    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailPattern.test(email)) {
      return { valid: false, message: 'Invalid email format' };
    }

    return { valid: true };
  },

  /**
   * Validate phone number
   */
  validatePhone: (phone) => {
    if (!phone) {
      return { valid: true }; // Phone is optional
    }

    // Remove spaces, dashes, parentheses
    const cleaned = phone.replace(/[\s\-()]/g, '');

    // Must be digits only (possibly with + prefix)
    const phonePattern = /^\+?\d{10,15}$/;
    if (!phonePattern.test(cleaned)) {
      return { valid: false, message: 'Phone number must be 10-15 digits' };
    }

    return { valid: true };
  },

  /**
   * Validate tax number format
   */
  validateTaxNumber: (taxNumber, country = 'ZA') => {
    if (!taxNumber) {
      return { valid: true }; // Tax number is optional
    }

    if (country === 'ZA') {
      // South African tax number: 10 digits
      if (!/^\d{10}$/.test(taxNumber)) {
        return { valid: false, message: 'South African tax number must be 10 digits' };
      }
    }

    return { valid: true };
  },

  /**
   * Validate registration number format
   */
  validateRegistrationNumber: (regNumber) => {
    if (!regNumber || regNumber.trim().length === 0) {
      return { valid: false, message: 'Registration number is required' };
    }

    if (regNumber.length < 5) {
      return { valid: false, message: 'Registration number must be at least 5 characters' };
    }

    return { valid: true };
  },
};

/**
 * Payment validation rules
 */
export const paymentValidation = {
  /**
   * Validate payment amount matches schedule
   */
  validatePaymentAmount: (paymentAmount, scheduledAmount, tolerance = 0.01) => {
    const paid = parseFloat(paymentAmount);
    const scheduled = parseFloat(scheduledAmount);

    if (isNaN(paid) || paid <= 0) {
      return { valid: false, message: 'Payment amount must be greater than zero' };
    }

    const difference = Math.abs(paid - scheduled);
    const percentDiff = (difference / scheduled) * 100;

    if (percentDiff > tolerance) {
      return {
        valid: true,
        warning: `Payment amount differs from scheduled amount by ${percentDiff.toFixed(2)}%`,
      };
    }

    return { valid: true };
  },

  /**
   * Validate payment date
   */
  validatePaymentDate: (paymentDate, scheduledDate) => {
    const paid = new Date(paymentDate);
    const scheduled = new Date(scheduledDate);
    const today = new Date();

    if (isAfter(paid, today)) {
      return { valid: false, message: 'Payment date cannot be in the future' };
    }

    // Warning if payment is more than 7 days late
    const daysDiff = differenceInDays(paid, scheduled);
    if (daysDiff > 7) {
      return {
        valid: true,
        warning: `Payment is ${daysDiff} days late`,
      };
    }

    // Warning if payment is early (before scheduled date)
    if (daysDiff < 0) {
      return {
        valid: true,
        warning: `Payment is ${Math.abs(daysDiff)} days early`,
      };
    }

    return { valid: true };
  },
};

/**
 * User validation rules
 */
export const userValidation = {
  /**
   * Validate user email
   */
  validateUserEmail: (email) => {
    if (!email || email.trim().length === 0) {
      return { valid: false, message: 'Email is required' };
    }

    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailPattern.test(email)) {
      return { valid: false, message: 'Invalid email format' };
    }

    return { valid: true };
  },
};

export default {
  contractValidation,
  stakeholderValidation,
  paymentValidation,
  userValidation,
  formatNumber,
  formatCurrency,
};
